// components/LiveTriggerFeed.jsx
//
// Real-time stream of scanner trigger events.
// Subscribes to socket.io channel `scanner:user:{userId}:trigger`.
// Backend publishes to `scanner:user:{userId}:trigger` whenever any of the
// user's profiles fires.

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, Typography, Paper, Chip, IconButton, Tooltip, Stack,
  FormControl, Select, MenuItem,
} from '@mui/material';
import {
  Bolt, FilterAlt, Pause, PlayArrow, Clear, OpenInNew, ContentCopy,
} from '@mui/icons-material';
import { io } from 'socket.io-client';

/* ═══════════════════════════════════════════════════════════════════════════════
   THEME
   ═══════════════════════════════════════════════════════════════════════════════ */

const C = {
  bg0: '#070a0f', bg1: '#070a0f', bg2: '#0f1218', bg3: '#0f1218', bg4: '#0b0e15',
  b0: 'rgba(255,255,255,0.06)', b2: 'rgba(255,255,255,0.1)',
  t1: '#f2f6ff', t2: '#e8edf7', t3: '#9aa6bd', t4: '#6f7c96',
  green: '#46b88a', red: '#d97b72', blue: '#4d8fc7', yellow: '#d9a441',
  purple: '#4d8fc7', orange: '#d9a441',
  longDim: 'rgba(70,184,138,0.08)', longBdr: 'rgba(70,184,138,0.25)',
  scannerDim: 'rgba(77,143,199,0.08)', scannerBdr: 'rgba(77,143,199,0.25)',
  mono: 'Roboto, "Helvetica Neue", Arial, sans-serif',
};

const panelSx = {
  bgcolor: C.bg3, border: '1px solid', borderColor: C.b0, borderRadius: 2, overflow: 'hidden',
};

const headerBarSx = {
  display: 'flex', alignItems: 'center',
  px: 2, py: 1, bgcolor: C.bg1,
  borderBottom: '1px solid', borderColor: C.b0, minHeight: 40,
};

const microLabel = {
  fontFamily: C.mono, fontSize: 10, fontWeight: 600, color: C.t4,
  textTransform: 'uppercase', letterSpacing: '0.08em',
};


const MAX_EVENTS = 200;

const METRIC_COLORS = {
  expression:          { color: C.green,  label: 'EXPR' },
  volume_spike:        { color: C.blue,   label: 'VOL'  },
  liquidation_cascade: { color: C.orange, label: 'LIQ'  },
  composite:           { color: C.purple, label: 'COMP' },
};


/* ═══════════════════════════════════════════════════════════════════════════════
   MAIN
   ═══════════════════════════════════════════════════════════════════════════════ */

export default function LiveTriggerFeed({ profiles = [], spawnedBots = {} }) {
  const [events, setEvents] = useState([]);
  const [paused, setPaused] = useState(false);
  const [profileFilter, setProfileFilter] = useState('all');
  const [metricFilter, setMetricFilter] = useState('all');
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);

  // ── Connect to socket.io feed ───────────────────────────────────────────────
  useEffect(() => {
    const userId = parseInt(localStorage.getItem('userId'), 10);
    if (!userId) return;

    const URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:3005';
    const socket = io(URL, { transports: ['websocket'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      socket.emit('joinScannerFeed', userId);
    });
    socket.on('disconnect', () => setConnected(false));

    const channel = `scanner:trigger:${userId}`;
    const handler = (raw) => {
      let event = raw;
      if (typeof raw === 'string') {
        try { event = JSON.parse(raw); } catch { return; }
      }
      if (!paused) {
        setEvents(prev => {
          // Dedup the same trigger arriving via both the join backfill and the
          // live relay (a sub-second race during join). triggerId is stable.
          if (event.triggerId && prev.some(e => e.triggerId === event.triggerId)) return prev;
          const next = [{ ...event, _localId: `${Date.now()}-${Math.random()}` }, ...prev];
          return next.length > MAX_EVENTS ? next.slice(0, MAX_EVENTS) : next;
        });
      }
    };
    socket.on(channel, handler);
    socket.on('scanner_trigger', handler); // fallback channel name

    return () => {
      socket.off(channel, handler);
      socket.off('scanner_trigger', handler);
      socket.emit('leaveScannerFeed', userId);
      socket.disconnect();
    };
  }, [paused]);

  // ── Filters ────────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    return events.filter(e => {
      if (profileFilter !== 'all' && e.profileId !== profileFilter) return false;
      if (metricFilter !== 'all' && e.metric !== metricFilter) return false;
      return true;
    });
  }, [events, profileFilter, metricFilter]);

  // Counts per profile (for sidebar)
  const counts = useMemo(() => {
    const out = { all: events.length };
    for (const e of events) {
      out[e.profileId] = (out[e.profileId] || 0) + 1;
    }
    return out;
  }, [events]);

  const profileById = useMemo(() => {
    const m = new Map();
    for (const p of profiles) m.set(p.id, p);
    return m;
  }, [profiles]);


  return (
    <Paper elevation={0} sx={panelSx}>
      {/* Header bar */}
      <Box sx={headerBarSx}>
        <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: connected ? C.green : C.t4, mr: 1, opacity: 0.8 }} />
        <Typography sx={{ fontFamily: C.mono, fontSize: 12, fontWeight: 700, color: C.t1, letterSpacing: '0.05em' }}>
          LIVE TRIGGERS
        </Typography>
        <Chip
          size="small"
          label={connected ? 'CONNECTED' : 'DISCONNECTED'}
          sx={{
            ml: 1, height: 18, fontFamily: C.mono, fontSize: 9, fontWeight: 700,
            bgcolor: connected ? C.longDim : 'transparent',
            color: connected ? C.green : C.t4,
            border: '1px solid', borderColor: connected ? C.longBdr : C.b0,
          }}
        />

        <Box sx={{ flex: 1 }} />

        <Typography sx={{ fontFamily: C.mono, fontSize: 11, color: C.t3, mr: 2 }}>
          {filtered.length} of {events.length} events
        </Typography>

        <Tooltip title={paused ? 'Resume stream' : 'Pause stream'} arrow>
          <IconButton size="small" onClick={() => setPaused(p => !p)} sx={{ color: paused ? C.yellow : C.t3, mr: 0.5 }}>
            {paused ? <PlayArrow sx={{ fontSize: 16 }} /> : <Pause sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Clear" arrow>
          <IconButton size="small" onClick={() => setEvents([])} sx={{ color: C.t3 }}>
            <Clear sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Filters */}
      <Box sx={{
        px: 2, py: 1.25, display: 'flex', gap: 1.5, alignItems: 'center',
        bgcolor: C.bg1, borderBottom: '1px solid', borderColor: C.b0,
      }}>
        <FilterAlt sx={{ fontSize: 14, color: C.t4 }} />

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <Select
            value={profileFilter}
            onChange={e => setProfileFilter(e.target.value)}
            sx={{
              fontFamily: C.mono, fontSize: 11, height: 28,
              bgcolor: C.bg3, color: C.t2,
              '& fieldset': { borderColor: C.b0 },
              '&:hover fieldset': { borderColor: `${C.b2} !important` },
            }}
            MenuProps={{ PaperProps: { sx: { bgcolor: C.bg1, border: '1px solid', borderColor: C.b2 } } }}
          >
            <MenuItem value="all" sx={{ fontFamily: C.mono, fontSize: 12 }}>
              All profiles ({counts.all || 0})
            </MenuItem>
            {profiles.map(p => (
              <MenuItem key={p.id} value={p.id} sx={{ fontFamily: C.mono, fontSize: 12 }}>
                {p.name} ({counts[p.id] || 0})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={metricFilter}
            onChange={e => setMetricFilter(e.target.value)}
            sx={{
              fontFamily: C.mono, fontSize: 11, height: 28,
              bgcolor: C.bg3, color: C.t2,
              '& fieldset': { borderColor: C.b0 },
              '&:hover fieldset': { borderColor: `${C.b2} !important` },
            }}
            MenuProps={{ PaperProps: { sx: { bgcolor: C.bg1, border: '1px solid', borderColor: C.b2 } } }}
          >
            <MenuItem value="all" sx={{ fontFamily: C.mono, fontSize: 12 }}>All types</MenuItem>
            {Object.entries(METRIC_COLORS).map(([k, v]) => (
              <MenuItem key={k} value={k} sx={{ fontFamily: C.mono, fontSize: 12 }}>{v.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Event list */}
      <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
        {filtered.length === 0 ? (
          <EmptyState connected={connected} hasEvents={events.length > 0} />
        ) : (
          filtered.map(ev => (
            <TriggerEventRow
              key={ev._localId}
              event={ev}
              profile={profileById.get(ev.profileId)}
              spawnedBot={(spawnedBots[ev.profileId] || []).find(b => b.symbol === ev.symbol && Math.abs((new Date(b.created_at).getTime() || 0) - ev.timestamp) < 5000)}
            />
          ))
        )}
      </Box>
    </Paper>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════════
   EMPTY STATE
   ═══════════════════════════════════════════════════════════════════════════════ */

function EmptyState({ connected, hasEvents }) {
  return (
    <Box sx={{ p: 6, textAlign: 'center' }}>
      <Bolt sx={{ fontSize: 32, color: C.t4, mb: 1.5 }} />
      <Typography sx={{ fontFamily: C.mono, fontSize: 12, color: C.t3, mb: 0.5 }}>
        {hasEvents ? 'No events match current filters' : 'Waiting for triggers'}
      </Typography>
      <Typography sx={{ fontFamily: C.mono, fontSize: 10, color: C.t4 }}>
        {connected ? 'Stream connected — events will appear here as they fire' : 'Stream disconnected — check your connection'}
      </Typography>
    </Box>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════════
   TRIGGER EVENT ROW
   ═══════════════════════════════════════════════════════════════════════════════ */

function TriggerEventRow({ event, profile, spawnedBot }) {
  const metric = event.metric || 'expression';
  const m = METRIC_COLORS[metric] || METRIC_COLORS.expression;
  const ts = new Date(event.timestamp);
  const timeStr = `${String(ts.getHours()).padStart(2, '0')}:${String(ts.getMinutes()).padStart(2, '0')}:${String(ts.getSeconds()).padStart(2, '0')}.${String(ts.getMilliseconds()).padStart(3, '0').slice(0, 2)}`;

  const valueDisplay = formatValue(event.value, metric);

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: '90px 70px 130px 1fr 100px 30px',
      gap: 1.5, alignItems: 'center',
      px: 2, py: 1.25,
      borderBottom: '1px solid', borderColor: C.b0,
      borderLeft: `2px solid ${m.color}`,
      transition: 'background 0.15s',
      '&:hover': { bgcolor: C.bg2 },
    }}>
      {/* Time */}
      <Typography sx={{ fontFamily: C.mono, fontSize: 11, color: C.t4 }}>
        {timeStr}
      </Typography>

      {/* Metric */}
      <Chip
        size="small"
        label={m.label}
        sx={{
          height: 18, fontFamily: C.mono, fontSize: 9, fontWeight: 700,
          bgcolor: 'transparent', color: m.color,
          border: '1px solid', borderColor: m.color,
          '& .MuiChip-label': { px: 0.75 },
        }}
      />

      {/* Symbol */}
      <Typography sx={{ fontFamily: C.mono, fontSize: 13, fontWeight: 600, color: C.t1 }}>
        {event.symbol}
      </Typography>

      {/* Profile + value preview */}
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{
          fontFamily: C.mono, fontSize: 11, fontWeight: 500, color: C.t2,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {profile?.name || `Profile ${event.profileId?.slice(0, 8)}`}
        </Typography>
        <Typography sx={{
          fontFamily: C.mono, fontSize: 10, color: C.t4,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {valueDisplay}
        </Typography>
      </Box>

      {/* Price + spawn status */}
      <Box sx={{ textAlign: 'right' }}>
        <Typography sx={{ fontFamily: C.mono, fontSize: 12, fontWeight: 600, color: C.t2 }}>
          {event.currentPrice != null
            ? Number(event.currentPrice).toLocaleString(undefined, { maximumFractionDigits: 8 })
            : '—'}
        </Typography>
        {spawnedBot ? (
          <Typography sx={{ fontFamily: C.mono, fontSize: 9, color: C.green, mt: '2px' }}>
            ↳ bot spawned
          </Typography>
        ) : (
          <Typography sx={{ fontFamily: C.mono, fontSize: 9, color: C.t4, mt: '2px' }}>
            no bot
          </Typography>
        )}
      </Box>

      {/* Actions */}
      <Tooltip title="Copy event JSON" arrow>
        <IconButton
          size="small"
          onClick={() => navigator.clipboard.writeText(JSON.stringify(event, null, 2))}
          sx={{ p: '3px', color: C.t4, '&:hover': { color: C.t2 } }}
        >
          <ContentCopy sx={{ fontSize: 12 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════════════════════════════ */

function formatValue(value, metric) {
  if (value == null) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);

  if (metric === 'volume_spike') return `${n.toFixed(2)}× baseline`;
  if (metric === 'liquidation_cascade') {
    return `$${n >= 1e6 ? `${(n / 1e6).toFixed(2)}M` : n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }
  // Expression — value is the eval result (boolean/number)
  if (n === 1 || n === 0) return n === 1 ? 'fired' : 'false';
  return n.toFixed(4);
}